"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Action() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="call-to-action" data-nav="light" className="bg-background py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-9 lg:px-16">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="relative overflow-hidden rounded-2xl bg-secondary px-6 py-16 text-secondary-foreground shadow-2xl sm:px-12 md:py-20 lg:px-20">
          {/* Subtle architectural frosted accent plinths */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 size-80 rounded-full bg-primary/10 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-20 size-96 rounded-full bg-white/5 blur-2xl" />

          <div className="relative mx-auto max-w-3xl text-center">
            {/* Craftsmanship Badge */}
            <div className="mb-6 flex justify-center">
              <Badge variant="outline" className="rounded-full border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold tracking-widest text-primary uppercase">
                Begin your project
              </Badge>
            </div>

            {/* Editorial Serif Heading */}
            <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              Plan your Brookhaven remodel with confidence
            </h2>

            {/* Architectural accent hairline divider */}
            <div aria-hidden="true" className="mx-auto my-6 h-[1px] w-24 bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

            {/* Warm ivory body text */}
            <p className="mx-auto max-w-2xl text-base leading-relaxed text-secondary-foreground/80 sm:text-lg">
              Schedule an in-home feasibility consultation — we will follow up to discuss your project, evaluate your floor plan, clarify municipal permitting pathways, and build a transparent preliminary project schedule for your home.
            </p>

            {/* Action Buttons Cluster */}
            <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-5">
              <motion.div whileHover={shouldReduceMotion ? undefined : {
              y: -4
            }} whileTap={shouldReduceMotion ? undefined : {
              scale: 0.98
            }} className="w-full sm:w-auto">
                <Button asChild size="lg" className="w-full rounded-lg border border-primary/80 bg-primary px-8 py-4 font-semibold text-primary-foreground shadow-lg hover:bg-primary/90 hover:shadow-xl sm:w-auto">
                  <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-2">
                    <span>Request an estimate</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div whileHover={shouldReduceMotion ? undefined : {
              y: -4
            }} whileTap={shouldReduceMotion ? undefined : {
              scale: 0.98
            }} className="w-full sm:w-auto">
                <Button asChild variant="outline" size="lg" className="w-full rounded-lg border-white/20 bg-white/5 px-7 py-4 font-medium text-secondary-foreground shadow-xs hover:border-primary/40 hover:bg-white/10 sm:w-auto">
                  <Link to="/AboutPage">Explore our process</Link>
                </Button>
              </motion.div>
            </div>

            {/* Verification & Direct Contact Details */}
            <div className="mt-12 flex flex-col items-center justify-center gap-6 border-t border-white/10 pt-8 text-sm text-secondary-foreground/75 sm:flex-row sm:gap-10">
              <a href="tel:+14708479076" className="inline-flex items-center gap-2.5 transition-colors hover:text-primary" aria-label="Call Brookhaven Elevation Renovations at (470) 847-9076">
                <Phone className="size-4 text-primary" />
                <span className="font-medium">(470) 847-9076</span>
              </a>

              <div className="hidden h-4 w-[1px] bg-white/15 sm:block" aria-hidden="true" />

              <div className="inline-flex items-center gap-2.5">
                <MapPin className="size-4 text-primary" />
                <span>Brookhaven, GA & surrounding North Atlanta</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}